<template>
  <div data-test-id="form-simplified-enrollment-mobile">
    <vf-notification
      v-if="notification.message"
      class="mb-4"
      :type="notification.type"
      @close="notification.message = ''"
    >
      {{ notification.message }}
    </vf-notification>
    <vf-progress-bar max="3" value="1" />
    <h2 class="mt-4" :class="brandClasses.subheading">
      {{ $t.mobileNumber }}
    </h2>
    <base-form
      ref="formRef"
      class="mt-4 space-y-4"
      :form
      @submit="submit"
    >
      <vf-form-field
        v-slot="{ attrs }"
        :hint="$t.simplifiedEnrollment.mobileNumberHint"
        name="phone"
        :rule="[validateRequired($t.mobileNumber), validatePhone(form.phoneCode)]"
      >
        <input-phone
          v-model="form.phone"
          v-model:code="form.phoneCode"
          v-bind="attrs"
          :disable-code="!mobilePhone.enableCountryCodeSelection"
          required
        />
      </vf-form-field>
      <vf-form-field v-slot="{ invalid }" name="accepted" :rule="validateRequired('', $t.validators.requiredCheckbox)">
        <vf-checkbox v-model="form.accepted" data-test-id="form-simplified-enrollment-mobile-checkbox" :invalid required size="sm">
          <form-legal-disclaimer :content="$t.smsTerms" />
        </vf-checkbox>
      </vf-form-field>

      <div class="mt-6 space-y-2">
        <vf-button class="w-full" :loading type="submit">
          {{ $t.next }}
        </vf-button>
        <vf-button class="w-full" :disabled="loading" variant="secondary" @click="emit('continue', false)">
          {{ $t.skipForNow }}
        </vf-button>
      </div>
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { BaseNotification } from '#types/notification'

const emit = defineEmits<{
  continue: [submitted: boolean]
}>()

const { brandClasses } = useAppConfig().components.dialog.simplifiedEnrollment
const { mobilePhone } = useAppConfig().components.form.signUp
const { validatePhone, validateRequired } = useLocalizedValidators()
const user = useUserStore()
const subscriptions = useSubscriptions()

const loading = ref(false)

const notification = reactive<BaseNotification>({
  message: '',
  type: 'info'
})

const formRef = ref<InstanceType<typeof BaseFormType>>()
const form = reactive({
  phone: '',
  phoneCode: getPhoneCodeByCountryCode(useCountryCode()) || '',
  accepted: false
})

const submit = async () => {
  try {
    loading.value = true
    notification.message = ''

    const phone = formatLocalNumberToE164(form.phone, form.phoneCode)

    await user.updateBasicInfo({
      phone,
      phoneCode: form.phoneCode
    })

    await subscriptions.sms(phone, form.phoneCode, true)

    emit('continue', true)
  }
  catch (err) {
    assertApiError(err)
    notification.message = err.message
    notification.type = 'error'
  }
  finally {
    loading.value = false
  }
}
</script>
